import React, { useState, useEffect, useMemo } from "react";
import { COPY_STRINGS } from "Constants/translatedCopy";
import {
  getRandomElements,
  getRandomInt,
  shuffle,
  sleep,
} from "../shared/util";
import useTimer from "../shared/useTimer";
import { Container, Image, Box } from "../shared";
import { getAudioForPhonemes } from "../shared/phonemeToAudio";
import useConversation from "../shared/useConversation";
import useValidation from "../shared/useValidation";
import ListeningStatus from "Components/Global/ListeningStatus";
import { listen } from "Services/Listen";
import { getUserData } from "Services/API";
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions";

import "./WordInvestigator.scss";
import icon from "./wordinvestigator-icon.png";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

export default function WordInvestigatorValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.word_investigator;

  const n = props.word.length;
  const isCorrectLength = n >= 3 && n <= 7;

  // letter count must equal phoneme count
  const { PHON } = props.wordMetadata;
  const nPhonemes = getAudioForPhonemes(PHON, getUserData().locale).length;
  const hasOnePhonemePerLetter = props.word.length === nPhonemes;

  const isValid =
    useValidation(props.version, props.wordMetadata, {
      1: ["IMG_URL", "PHON"],
      2: ["IMG_URL"],
    }) &&
    isCorrectLength &&
    hasOnePhonemePerLetter;

  const isMountedRef = useIsMounted();

  useEffect(() => {
    if (!isValid) close();
  }, []);

  if (!isValid) return null;

  const Version = {
    1: WordInvestigator,
    2: WordInvestigator,
  }[props.version];

  return <Version {...props} close={close} versionId={props.version} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }
}

function WordInvestigator(props) {
  const { word, wordMetadata, speech, wcpm, versionId } = props;
  const { IMG_URL, PHON } = wordMetadata;

  const [stage, setStage] = useState(0);
  const [canClick, setCanClick] = useState(false);
  const [revealAnswer, setRevealAnswer] = useState(false);
  const [didSelectTarget, setDidSelectTarget] = useState(false);
  const [wrongLetter, setWrongLetter] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const maxTime = useTimer({
    timeout: 20000,
    onEnd: giveCorrectAnswer,
  });

  const inactivity = useTimer({
    timeout: 5000,
    onEnd: async () => {
      setCanClick(false);
      await speak(ttsStrings.inactivity);
      setCanClick(true);
    },
  });

  const { speak, speakSequence, speakFile, speakPhonemes, listenWord } = useConversation(
    speech,
    listen
  );
  const { cancellablePromise } = useCancellablePromise();

  const { options, target, targetIndex } = useMemo(() => {
    const targetIndex = getTargetIndex(word, wcpm, versionId);
    const target = word[targetIndex];
    const distractors = getDistractors(target);
    const options = shuffle([...distractors, target]);
    return { options, target, targetIndex };
  }, []);

  const files = getAudioForPhonemes(PHON, getUserData().locale);

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          await cancellablePromise(speak(ttsStrings.intro({ word })));
          if (versionId === 1) {
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                {textToSpeak: word, skipTranslation: true},
                ttsStrings.describe_1({word}),
              ]));
            }
            else await cancellablePromise(speak(`${word} ${ttsStrings.describe_1({word})}`));

            await cancellablePromise(sleep());
            await cancellablePromise(speakPhonemes({ phon: PHON }));
            await cancellablePromise(speak(ttsStrings.describe_2({ word })));
            await cancellablePromise(sleep(500));
            await cancellablePromise(speakFile(files[targetIndex]));
            await cancellablePromise(speak(ttsStrings.missing));
          }
          else {
            if(getUserData().bilingualMode) {
              await cancellablePromise(speakSequence([
                ttsStrings.describe_3,
                {textToSpeak: word, skipTranslation: true},
              ]));
            }
            else await cancellablePromise(speak(`${ttsStrings.describe_3} ${word}.`));
          }
          await cancellablePromise(sleep());
          setStage(1);
          break;
        case 1:
          await cancellablePromise(speak(ttsStrings.ask_letter));
          setCanClick(true);
          maxTime.startTimer();
          inactivity.startTimer();
          break;
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      {renderContent()}
      <div style={{ marginTop: 30 }}>
        <ListeningStatus />
      </div>
    </Container>
  );

  function renderContent() {
    if (stage === 0) {
      return (
        <>
          <Image src={IMG_URL} style={{ height: 200, marginBottom: 20 }} />
          <div className="wi-boxes">
            {word.split("").map((char, i) => {
              let content = char;
              if (i === targetIndex) content = "";

              return (
                <Box key={i} className="wi-box">
                  <div className="wi-letter">{content}</div>
                </Box>
              );
            })}
          </div>
        </>
      );
    }

    if (stage === 1) {
      const optionStyle = {};
      if (!canClick) {
        optionStyle.cursor = "default";
        optionStyle.color = "#999";
      }
      return (
        <>
          <div className="wi-pool">
            {options.map((char, i) => {
              const onOptionClickHandler = async () => {
                if (!props.isMountedRef.current) return;
                try {
                  if (!canClick) return;
                  setCanClick(false);
                  inactivity.clearTimer();
                  if (char === target) {
                    // if correct
                    maxTime.clearTimer();
                    setRevealAnswer(true);
                    setDidSelectTarget(true);
                    await cancellablePromise(speak(ttsStrings.correct_1));
                    props.close();
                  } else {
                    // if incorrect
                    maxTime.restartTimer();
                    setWrongLetter(char);
                    await cancellablePromise(speak(ttsStrings.struggle));
                    await cancellablePromise(sleep(2000));
                    if (attempts === 0) {
                      await cancellablePromise(speak(ttsStrings.try_again));
                      setCanClick(true);
                      setAttempts(1);
                      setWrongLetter(null);
                    } else giveCorrectAnswer();
                  }
                } catch (error) {
                  if (error.isCancelled) return;
                  throw(error);
                }
              }

              return (
                <div
                  key={i}
                  className="wi-option"
                  style={optionStyle}
                  {...doNotUseAccessibleStaticElementInteractions({onClick: onOptionClickHandler})}
                >
                  {char}
                </div>
              )}
            )}
          </div>
          <div className="wi-boxes">
            {word.split("").map((char, i) => {
              const boxStyle = {};
              if (didSelectTarget) boxStyle.background = "#b5ffd5";
              let content = char;
              if (!revealAnswer && i === targetIndex) {
                content = "";
                if (wrongLetter) {
                  content = wrongLetter;
                  boxStyle.background = "#ffb2ad";
                }
              }
              return (
                <Box key={i} className="wi-box" style={boxStyle}>
                  <div className="wi-letter">{content}</div>
                </Box>
              );
            })}
          </div>
        </>
      );
    }
  }

  async function giveCorrectAnswer() {
    if (!props.isMountedRef.current) return;
    try {
      maxTime.clearTimer();
      setCanClick(false);
      setRevealAnswer(true);

      if(getUserData().bilingualMode) {
        await cancellablePromise(speakSequence([
          ttsStrings.give_answer_part_1,
          {textToSpeak: word, skipTranslation: true},
          ttsStrings.give_answer_part_2,
        ]));
      }
      else await cancellablePromise(speak(`${ttsStrings.give_answer_part_1} ${target}. ${ttsStrings.give_answer_part_2}`));

      const { status } = await cancellablePromise(listenWord({
        targetWord: word,
        patterOverride: ["match"],
      }));
      if (status === "match") await cancellablePromise(speak(ttsStrings.correct_2));
      props.close();
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }
}

function getTargetIndex(word, wcpm, versionId) {
  if (versionId === 1) {
    const pos = getTargetPosition(wcpm);
    if (pos === "s") return 0;
    if (pos === "e") return word.length - 1;
  }

  // randomly choose one in the middle
  return getRandomInt(1, word.length - 2);
}

function getTargetPosition(wcpm) {
  let threshold = 50;
  if (wcpm >= 15) threshold = 35;
  if (wcpm >= 25) threshold = 30;
  if (wcpm >= 50) threshold = 25;

  const i = Math.floor(Math.random() * 100);

  let pos = "s";
  if (i >= threshold) pos = "e";
  return pos;
}

function getDistractors(target) {
  const isVowel = vowels.includes(target);
  if (isVowel) {
    return [
      ...getRandomLetters(vowels, target, 2),
      ...getRandomLetters(consonants, target, 2),
    ];
  } else {
    return [
      ...getRandomLetters(vowels, target, 1),
      ...getRandomLetters(consonants, target, 3),
    ];
  }
}

// pool: arr of letter to choose from
// target: target letter to filter out
// count: how many random letters to retrieve
function getRandomLetters(pool, target, count) {
  const options = pool.filter((c) => c != target);
  return getRandomElements(options, count);
}

const vowels = ["a", "e", "i", "o", "u"];
const consonants = [
  "b",
  "c",
  "d",
  "f",
  "g",
  "h",
  "j",
  "k",
  "l",
  "m",
  "n",
  "p",
  "r",
  "s",
  "t",
  "v",
  "w",
  "x",
  "z",
];
